// src/components/doctorComponents/ViewEditRecordModal.jsx - COMPLETE WITH SANITIZATION

import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  searchMedicines,
  getDoctorsList,
  updateMedicalRecord,
  clearMedicinesSearch,
  getMedicalRecordByAppointment,
} from "../../Redux/actions/doctorActions/doctorActions.js";
import { showToast } from "../ToastMessage.jsx";
import { useSanitizedInput } from "../../hooks/useSanitizedInput.js"; // ✅ Add import
import "./DoctorModals.css";
import { printPrescription } from './printPrescription';
import { convertTo12Hour } from "../../utils/timeUtils";

const debounce = (func, delay) => {
  let timeoutId;
  const debounced = (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
  debounced.cancel = () => clearTimeout(timeoutId);
  return debounced;
};

const ViewEditRecordModal = ({ appointment, onClose }) => {
  const dispatch = useDispatch();
  const {
    medicinesSearch = [],
    doctorsList = [],
    currentMedicalRecord,
    medicalRecordLoading,
  } = useSelector((state) => state.doctor || {});

  const [isEditing, setIsEditing] = useState(false);
  const [medicalRecord, setMedicalRecord] = useState(null);
   const [isPrinting, setIsPrinting] = useState(false); 

  // ✅ Replace form state with sanitized hooks
  const [chiefComplaint, handleChiefComplaintChange, resetChiefComplaint, setChiefComplaint] = useSanitizedInput("", "text");
  const [symptoms, handleSymptomsChange, resetSymptoms, setSymptoms] = useSanitizedInput("", "text");
  const [diagnosis, handleDiagnosisChange, resetDiagnosis, setDiagnosis] = useSanitizedInput("", "text");
  const [clinicalNotes, handleClinicalNotesChange, resetClinicalNotes, setClinicalNotes] = useSanitizedInput("", "text");
  const [physicalExamination, handlePhysicalExaminationChange, resetPhysicalExamination, setPhysicalExamination] = useSanitizedInput("", "text");
  const [followUpNotes, handleFollowUpNotesChange, resetFollowUpNotes, setFollowUpNotes] = useSanitizedInput("", "text");
  const [medicineSearchQuery, handleMedicineSearchChange, resetMedicineSearch, setMedicineSearchQuery] = useSanitizedInput("", "text");
  const [customMedicineName, handleCustomMedicineNameChange, resetCustomMedicine, setCustomMedicineName] = useSanitizedInput("", "alphanumeric");
  const [referralReason, handleReferralReasonChange, resetReferralReason, setReferralReason] = useSanitizedInput("", "text");

  const [prescribedMedicines, setPrescribedMedicines] = useState([]);
  const [labTests, setLabTests] = useState([]);
  const [isReferring, setIsReferring] = useState(false);
  const [referralDoctor, setReferralDoctor] = useState("");
  const [followUpDate, setFollowUpDate] = useState("");

  // Medicine search
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [showCustomMedicineForm, setShowCustomMedicineForm] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [currentTab, setCurrentTab] = useState("examination");

  // Load medical record
  useEffect(() => {
    const loadRecord = async () => {
      const record = await dispatch(
        getMedicalRecordByAppointment(appointment._id)
      );
      if (record) {
        setMedicalRecord(record);
        // ✅ Use direct setters for programmatic updates
        setChiefComplaint(record.chiefComplaint || "");
        setSymptoms(record.symptoms || "");
        setDiagnosis(record.diagnosis || "");
        setClinicalNotes(record.clinicalNotes || "");
        setPhysicalExamination(record.physicalExamination || "");
        setPrescribedMedicines(record.medicines || []);
        setLabTests(record.labTests || []);
        setIsReferring(record.referral?.isReferred || false);
        setReferralDoctor(record.referral?.referredTo?._id || "");
        setReferralReason(record.referral?.reason || "");
        setFollowUpDate(
          record.followUpDate ? record.followUpDate.split("T")[0] : ""
        );
        setFollowUpNotes(record.followUpNotes || "");
      }
    };

    loadRecord();
    dispatch(getDoctorsList());
  }, [appointment._id, dispatch, setChiefComplaint, setSymptoms, setDiagnosis, setClinicalNotes, setPhysicalExamination, setReferralReason, setFollowUpNotes]);

  // ✅ Add print handler
  const handlePrint = async () => {
    if (!medicalRecord) {
      showToast("No medical record to print");
      return;
    }

    try {
      setIsPrinting(true);
      await printPrescription(medicalRecord, appointment);
      showToast("Prescription printed successfully!");
    } catch (error) {
      console.error("Print error:", error);
      showToast(error.message || "Failed to print prescription");
    } finally {
      setIsPrinting(false);
    }
  };

  const doSearch = useCallback(
    debounce((query) => {
      if (query && query.trim().length >= 2) {
        dispatch(searchMedicines(query.trim()));
      }
    }, 300),
    [dispatch]
  );

  useEffect(() => {
    if (medicineSearchQuery.trim().length >= 2) {
      doSearch(medicineSearchQuery);
      setShowSearchDropdown(true);
    } else {
      setShowSearchDropdown(false);
    }

    return () => {
      doSearch.cancel();
    };
  }, [medicineSearchQuery, doSearch]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".position-relative")) {
        setShowSearchDropdown(false);
      }
    };

    if (showSearchDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSearchDropdown]);

  const addMedicine = (medicine) => {
    const exists = prescribedMedicines.find(
      (m) => m.medicineName.toLowerCase() === medicine.name.toLowerCase()
    );
    if (exists) {
      showToast("Medicine already added");
      return;
    }

    setPrescribedMedicines([
      ...prescribedMedicines,
      {
        medicine: medicine._id || null,
        medicineName: medicine.name,
        company: medicine.company || "",
        currentStock: medicine.stock || 0,
        dosage: "",
        frequency: "",
        duration: "",
        instructions: "",
        route: "",
        isCustom: false,
      },
    ]);
    resetMedicineSearch(); // ✅ Use hook's reset
    setShowSearchDropdown(false);
    dispatch(clearMedicinesSearch());
    showToast(`${medicine.name} added to prescription`);
  };

  const addCustomMedicine = () => {
    if (!customMedicineName.trim()) {
      showToast("Please enter medicine name");
      return;
    }

    // ✅ Check length after sanitization
    if (customMedicineName.length > 200) {
      showToast("Medicine name is too long (max 200 characters)");
      return;
    }

    const exists = prescribedMedicines.find(
      (m) => m.medicineName.toLowerCase() === customMedicineName.toLowerCase()
    );
    if (exists) {
      showToast("Medicine already added");
      return;
    }

    setPrescribedMedicines([
      ...prescribedMedicines,
      {
        medicine: null,
        medicineName: customMedicineName.trim(),
        company: "",
        currentStock: 0,
        dosage: "",
        frequency: "",
        duration: "",
        instructions: "",
        route: "",
        isCustom: true,
      },
    ]);

    resetCustomMedicine(); // ✅ Use hook's reset
    setShowCustomMedicineForm(false);
    showToast("Custom medicine added");
  };

  const updateMedicine = (index, field, value) => {
    const updated = [...prescribedMedicines];
    
    // ✅ Sanitize medicine field updates
    if (field === "dosage" || field === "frequency" || field === "duration" || field === "route") {
      value = value.replace(/[<>]/g, '').substring(0, 100);
    } else if (field === "instructions") {
      value = value.replace(/[<>]/g, '').substring(0, 500);
    }
    
    updated[index][field] = value;
    setPrescribedMedicines(updated);
  };

  const removeMedicine = (index) => {
    setPrescribedMedicines(prescribedMedicines.filter((_, i) => i !== index));
  };

  const addLabTest = () => {
    setLabTests([
      ...labTests,
      {
        testName: "",
        urgent: false,
        resultRemarks: "",
      },
    ]);
  };

  const updateLabTest = (index, field, value) => {
    const updated = [...labTests];
    
    // ✅ Sanitize lab test field updates
    if (field === "testName") {
      value = value.replace(/[<>]/g, '').substring(0, 200);
    // ✅ Auto-fill testName when testType changes
    if (field === "testType") {
      updated[index].testName = value;
    }
    } else if (field === "resultRemarks") {
      value = value.replace(/[<>]/g, '').substring(0, 1000);
    }
    
    updated[index][field] = value;
    setLabTests(updated);
  };

  const removeLabTest = (index) => {
    setLabTests(labTests.filter((_, i) => i !== index));
  };

  const handleUpdate = async () => {
    // ✅ Validate length limits
    if (chiefComplaint.length > 500) {
      showToast("Chief complaint is too long (max 500 characters)");
      return;
    }

    if (diagnosis.length > 500) {
      showToast("Diagnosis is too long (max 500 characters)");
      return;
    }

    if (symptoms.length > 1000) {
      showToast("HOPC is too long (max 1000 characters)");
      return;
    }

    if (clinicalNotes.length > 2000) {
      showToast("Clinical notes are too long (max 2000 characters)");
      return;
    }

    if (physicalExamination.length > 1000) {
      showToast("Physical examination is too long (max 1000 characters)");
      return;
    }

    if (followUpNotes.length > 500) {
      showToast("Follow-up notes are too long (max 500 characters)");
      return;
    }

    if (!chiefComplaint.trim() || !diagnosis.trim()) {
      showToast("Chief complaint and diagnosis are required");
      return;
    }

    // ✅ Validate medicines array
    if (prescribedMedicines.length > 50) {
      showToast("Too many medicines (max 50)");
      return;
    }

    // ✅ Validate lab tests array
    if (labTests.length > 30) {
      showToast("Too many lab tests (max 30)");
      return;
    }

    const recordData = {
      chiefComplaint,
      symptoms,
      diagnosis,
      clinicalNotes,
      physicalExamination,
      medicines: prescribedMedicines,
      labTests,
      referral: isReferring
        ? {
            isReferred: true,
            referredTo: referralDoctor,
            reason: referralReason,
          }
        : { isReferred: false },
      followUpDate: followUpDate || null,
      followUpNotes,
    };

    try {
      setSubmitting(true);
      await dispatch(updateMedicalRecord(medicalRecord._id, recordData));
      // ✅ Reload the updated record after save
      const updatedRecord = await dispatch(getMedicalRecordByAppointment(appointment._id));
      if (updatedRecord) {
        setMedicalRecord(updatedRecord);
      }
      showToast("Medical record updated successfully!");
      setIsEditing(false);
    } catch (error) {
      showToast("Failed to update medical record");
    } finally {
      setSubmitting(false);
    }
  };

  if (medicalRecordLoading || !medicalRecord) {
    return (
      <div className="modal-overlay">
        <div className="modal-container">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading medical record...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal-container modal-xl">
        <div className="modal-header-custom">
          <h4>
            <i className="bi bi-file-medical me-2"></i>
            Medical Record - {appointment.pName}
          </h4>
          <div className="d-flex gap-2 align-items-center">
            <button
              className="btn btn-success btn-sm"
              onClick={handlePrint}
              disabled={isPrinting}
            >
              {isPrinting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1"></span>
                  Printing...
                </>
              ) : (
                <>
                  <i className="bi bi-printer me-1"></i>
                  Print
                </>
              )}
            </button>
            {!isEditing && (
              <button
                className="btn btn-warning btn-sm"
                onClick={() => setIsEditing(true)}
              >
                <i className="bi bi-pencil me-1"></i>
                Edit
              </button>
            )}
          </div>
        </div>

        <div className="patient-info-banner">
          <div className="row">
            <div className="col-md-3">
              <strong>MR Code:</strong> <code>{medicalRecord.mrCode}</code>
            </div>
            <div className="col-md-3">
              <strong>Date:</strong>{" "}
              {new Date(medicalRecord.createdAt).toLocaleDateString()}
            </div>
            {appointment.time && (
                <div className="col-md-3">
                  <strong>Time:</strong>{" "}
                  {convertTo12Hour(appointment.time)}
                </div>
              )}
            <div className="col-md-3">
              <strong>Doctor:</strong> {medicalRecord.doctorId?.name}
            </div>
            <div className="col-md-3">
              <strong>Department:</strong> {medicalRecord.doctorId?.department}
            </div>
          </div>
        </div>

        {/* Tabs - only show in edit mode */}
        {isEditing && (
          <ul className="nav nav-tabs mt-3">
            <li className="nav-item">
              <button
                className={`nav-link ${
                  currentTab === "examination" ? "active" : ""
                }`}
                onClick={() => setCurrentTab("examination")}
              >
                Examination
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${
                  currentTab === "prescription" ? "active" : ""
                }`}
                onClick={() => setCurrentTab("prescription")}
              >
                Prescription ({prescribedMedicines.length})
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${
                  currentTab === "labtests" ? "active" : ""
                }`}
                onClick={() => setCurrentTab("labtests")}
              >
                Lab Tests ({labTests.length})
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${
                  currentTab === "referral" ? "active" : ""
                }`}
                onClick={() => setCurrentTab("referral")}
              >
                Referral
              </button>
            </li>
          </ul>
        )}

        <div className="modal-body-custom">
          {/* VIEW MODE */}
          {!isEditing && (
            <div>
              <div className="mb-4">
                <h6 className="fw-bold text-primary">
                  <i className="bi bi-clipboard-pulse me-2"></i>
                  Chief Complaint
                </h6>
                <p className="ms-4">{medicalRecord.chiefComplaint}</p>
              </div>

              {medicalRecord.symptoms && (
                <div className="mb-4">
                  <h6 className="fw-bold text-primary">
                    <i className="bi bi-thermometer me-2"></i>
                    HOPC (History of Present Complaint)
                  </h6>
                  <p className="ms-4">{medicalRecord.symptoms}</p>
                </div>
              )}

              {medicalRecord.physicalExamination && (
                <div className="mb-4">
                  <h6 className="fw-bold text-primary">
                    <i className="bi bi-stethoscope me-2"></i>
                    Physical Examination
                  </h6>
                  <p className="ms-4">{medicalRecord.physicalExamination}</p>
                </div>
              )}

              <div className="mb-4">
                <h6 className="fw-bold text-primary">
                  <i className="bi bi-clipboard-check me-2"></i>
                  Diagnosis
                </h6>
                <p className="ms-4">{medicalRecord.diagnosis}</p>
              </div>

              {medicalRecord.clinicalNotes && (
                <div className="mb-4">
                  <h6 className="fw-bold text-primary">
                    <i className="bi bi-file-text me-2"></i>
                    Clinical Notes
                  </h6>
                  <p className="ms-4">{medicalRecord.clinicalNotes}</p>
                </div>
              )}

              {medicalRecord.medicines?.length > 0 && (
                <div className="mb-4">
                  <h6 className="fw-bold text-primary">
                    <i className="bi bi-capsule me-2"></i>
                    Prescription
                  </h6>
                  <div className="ms-4">
                    {medicalRecord.medicines.map((med, idx) => (
                      <div
                        key={idx}
                        className="border p-3 mb-2 rounded bg-light"
                      >
                        <div className="d-flex justify-content-between">
                          <div>
                            <strong className="text-dark">
                              {idx + 1}. {med.medicineName}
                            </strong>
                            {med.company && (
                              <span className="text-muted">
                                {" "}
                                - {med.company}
                              </span>
                            )}
                            {med.isCustom && (
                              <span className="badge bg-info ms-2">Custom</span>
                            )}
                          </div>
                        </div>
                        <div className="mt-2">
                          <small className="d-block">
                            <strong>Dosage:</strong> {med.dosage} |
                            <strong> Frequency:</strong> {med.frequency} |
                            <strong> Duration:</strong> {med.duration}
                          </small>
                          {med.timing && (
                            <small className="d-block">
                              <strong>Timing:</strong> {med.timing}
                            </small>
                          )}
                          {med.instructions && (
                            <small className="d-block text-muted">
                              <strong>Instructions:</strong> {med.instructions}
                            </small>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {medicalRecord.labTests?.length > 0 && (
                <div className="mb-4">
                  <h6 className="fw-bold text-primary">
                    <i className="bi bi-clipboard2-pulse me-2"></i>
                    Lab Tests
                  </h6>
                  <div className="ms-4">
                    {medicalRecord.labTests.map((test, idx) => (
                      <div
                        key={idx}
                        className="border p-3 mb-2 rounded bg-light"
                      >
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <strong className="text-dark">
                              {idx + 1}. {test.testName}
                            </strong>
                            <span className="badge bg-secondary ms-2">
                              {test.testType}
                            </span>
                            {test.urgent && (
                              <span className="badge bg-danger ms-2">
                                Urgent
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {test.resultRemarks && (
                          <div className="mt-2 p-2 bg-success bg-opacity-10 border border-success rounded">
                            <small className="text-success">
                              <i className="bi bi-clipboard-check me-1"></i>
                              <strong>Result:</strong> {test.resultRemarks}
                            </small>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {medicalRecord.referral?.isReferred && (
                <div className="mb-4">
                  <h6 className="fw-bold text-primary">
                    <i className="bi bi-arrow-right-circle me-2"></i>
                    Referral
                  </h6>
                  <div className="ms-4 border p-3 rounded bg-light">
                    <p className="mb-2">
                      <strong>Referred To:</strong>{" "}
                      {medicalRecord.referral.referredTo?.name ||
                        medicalRecord.referral.referredToDoctorName ||
                        "N/A"}
                    </p>
                    <p className="mb-2">
                      <strong>Department:</strong>{" "}
                      {medicalRecord.referral.referredTo?.department ||
                        medicalRecord.referral.referredToDepartment ||
                        "N/A"}
                    </p>
                    {medicalRecord.referral.reason && (
                      <p className="mb-0">
                        <strong>Reason:</strong> {medicalRecord.referral.reason}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {(medicalRecord.followUpDate || medicalRecord.followUpNotes) && (
                <div className="mb-4">
                  <h6 className="fw-bold text-primary">
                    <i className="bi bi-calendar-check me-2"></i>
                    Follow-up
                  </h6>
                  <div className="ms-4">
                    {medicalRecord.followUpDate && (
                      <p className="mb-2">
                        <strong>Date:</strong>{" "}
                        {new Date(
                          medicalRecord.followUpDate
                        ).toLocaleDateString()}
                      </p>
                    )}
                    {medicalRecord.followUpNotes && (
                      <p className="mb-0">
                        <strong>Notes:</strong> {medicalRecord.followUpNotes}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* EDIT MODE */}
          {isEditing && (
            <>
              {currentTab === "examination" && (
                <div>
                  <div className="mb-3">
                    <label className="form-label fw-bold">
                      Chief Complaint <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Main reason for visit"
                      value={chiefComplaint}
                      onChange={handleChiefComplaintChange} // ✅ Use sanitized handler
                      maxLength={500} // ✅ Add max length
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold">HOPC (History of Present Complaint)</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      placeholder="Describe symptoms"
                      value={symptoms}
                      onChange={handleSymptomsChange} // ✅ Use sanitized handler
                      maxLength={1000} // ✅ Add max length
                    ></textarea>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold">
                      Physical Examination
                    </label>
                    <textarea
                      className="form-control"
                      rows="3"
                      placeholder="Physical examination findings"
                      value={physicalExamination}
                      onChange={handlePhysicalExaminationChange} // ✅ Use sanitized handler
                      maxLength={1000} // ✅ Add max length
                    ></textarea>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold">
                      Diagnosis <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Final diagnosis"
                      value={diagnosis}
                      onChange={handleDiagnosisChange} // ✅ Use sanitized handler
                      maxLength={500} // ✅ Add max length
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold">Clinical Notes</label>
                    <textarea
                      className="form-control"
                      rows="4"
                      placeholder="Additional clinical notes"
                      value={clinicalNotes}
                      onChange={handleClinicalNotesChange} // ✅ Use sanitized handler
                      maxLength={2000} // ✅ Add max length
                    ></textarea>
                  </div>

                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-bold">
                        Follow-up Date
                      </label>
                      <input
                        type="date"
                        className="form-control"
                        value={followUpDate}
                        onChange={(e) => setFollowUpDate(e.target.value)}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-bold">
                        Follow-up Notes
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Follow-up instructions"
                        value={followUpNotes}
                        onChange={handleFollowUpNotesChange} // ✅ Use sanitized handler
                        maxLength={500} // ✅ Add max length
                      />
                    </div>
                  </div>
                </div>
              )}

              {currentTab === "prescription" && (
                <div>
                  <div className="mb-3">
                    <label className="form-label fw-bold">
                      Search Medicine Database
                    </label>
                    <div className="position-relative">
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Type medicine name to search..."
                          value={medicineSearchQuery}
                          onChange={handleMedicineSearchChange} // ✅ Use sanitized handler
                          maxLength={100} // ✅ Add max length
                        />
                        <button
                          className="btn btn-outline-primary"
                          onClick={() =>
                            setShowCustomMedicineForm(!showCustomMedicineForm)
                          }
                        >
                          <i className="bi bi-plus-circle me-1"></i>
                          Add Custom
                        </button>
                      </div>

                      {showSearchDropdown &&
                        medicineSearchQuery.length >= 2 && (
                          <div
                            className="medicine-dropdown"
                            style={{
                              zIndex: 1000,
                              maxHeight: "300px",
                              overflowY: "auto",
                            }}
                          >
                            {medicinesSearch && medicinesSearch.length > 0 ? (
                              medicinesSearch.map((med) => (
                                <div
                                  key={med._id}
                                  className="medicine-item p-3 border-bottom bg-white"
                                  onClick={() => addMedicine(med)}
                                  style={{ cursor: "pointer" }}
                                >
                                  <div className="d-flex justify-content-between align-items-start">
                                    <div>
                                      <strong>{med.name}</strong>
                                      {med.company && (
                                        <span className="text-muted">
                                          {" "}
                                          - {med.company}
                                        </span>
                                      )}
                                      <br />
                                      <small className="text-muted">
                                        Stock: {med.stock || 0} | Price: Rs.{" "}
                                        {med.price}
                                      </small>
                                    </div>
                                    {med.stock === 0 && (
                                      <span className="badge bg-danger">
                                        Out of Stock
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="p-3 bg-white border">
                                <small className="text-muted">
                                  <i className="bi bi-search me-2"></i>
                                  Searching...
                                </small>
                              </div>
                            )}
                          </div>
                        )}
                    </div>
                  </div>

                  {showCustomMedicineForm && (
                    <div className="card mb-3 border-primary">
                      <div className="card-body">
                        <h6 className="card-title">
                          <i className="bi bi-pencil-square me-2"></i>
                          Add Custom Medicine
                        </h6>
                        <div className="row">
                          <div className="col-md-8 mb-2">
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Enter medicine name"
                              value={customMedicineName}
                              onChange={handleCustomMedicineNameChange} // ✅ Use sanitized handler
                              onKeyPress={(e) =>
                                e.key === "Enter" && addCustomMedicine()
                              }
                              maxLength={200} // ✅ Add max length
                            />
                          </div>
                          <div className="col-md-4 mb-2">
                            <button
                              className="btn btn-primary me-2"
                              onClick={addCustomMedicine}
                            >
                              Add
                            </button>
                            <button
                              className="btn btn-secondary"
                              onClick={() => {
                                setShowCustomMedicineForm(false);
                                resetCustomMedicine(); // ✅ Use hook's reset
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="prescribed-medicines">
                    {prescribedMedicines.length === 0 ? (
                      <div className="text-center py-4 text-muted">
                        <i className="bi bi-capsule display-4"></i>
                        <p className="mt-2">No medicines prescribed yet</p>
                      </div>
                    ) : (
                      prescribedMedicines.map((med, idx) => (
                        <div key={idx} className="medicine-card">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div>
                              <h6 className="mb-0">
                                {med.medicineName}
                                {med.isCustom && (
                                  <span className="badge bg-info ms-2">
                                    Custom
                                  </span>
                                )}
                              </h6>
                              {med.company && (
                                <small className="text-muted">
                                  {med.company}
                                </small>
                              )}
                            </div>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => removeMedicine(idx)}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>

                          <div className="row">
                            <div className="col-md-3 mb-2">
                              <label className="form-label small">Dosage</label>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="e.g., 500mg"
                                value={med.dosage}
                                onChange={(e) =>
                                  updateMedicine(idx, "dosage", e.target.value)
                                }
                                maxLength={100} // ✅ Add max length
                              />
                            </div>
                            <div className="col-md-3 mb-2">
                              <label className="form-label small">
                                Frequency
                              </label>
                              <select
                                className="form-select form-select-sm"
                                value={med.frequency}
                                onChange={(e) =>
                                  updateMedicine(
                                    idx,
                                    "frequency",
                                    e.target.value
                                  )
                                }
                              >
                                <option value="">Select</option>
                                <option value="Once daily">Once daily</option>
                                <option value="Twice daily">Twice daily</option>
                                <option value="Three times daily">
                                  Three times daily
                                </option>
                                <option value="Four times daily">
                                  Four times daily
                                </option>
                              </select>
                            </div>
                            <div className="col-md-3 mb-2">
                              <label className="form-label small">
                                Duration
                              </label>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="e.g., 7 days"
                                value={med.duration}
                                onChange={(e) =>
                                  updateMedicine(
                                    idx,
                                    "duration",
                                    e.target.value
                                  )
                                }
                                maxLength={100} // ✅ Add max length
                              />
                            </div>
                            <div className="col-md-3 mb-2">
                          <label className="form-label small">Route</label>
                          <select
                            className="form-select form-select-sm"
                            value={med.route}
                            onChange={(e) => updateMedicine(idx, "route", e.target.value)}
                          >
                            <option value="">Select</option>
                            <option value="Oral">Oral</option>
                            <option value="IV">IV (Intravenous)</option>
                            <option value="IM">IM (Intramuscular)</option>
                            <option value="SC">SC (Subcutaneous)</option>
                            <option value="Topical">Topical</option>
                            <option value="Rectal">Rectal</option>
                            <option value="Inhalation">Inhalation</option>
                            <option value="Sublingual">Sublingual</option>
                          </select>
                        </div>
                            <div className="col-md-12 mb-2">
                              <label className="form-label small">
                                Instructions
                              </label>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="Additional instructions"
                                value={med.instructions}
                                onChange={(e) =>
                                  updateMedicine(
                                    idx,
                                    "instructions",
                                    e.target.value
                                  )
                                }
                                maxLength={500} // ✅ Add max length
                              />
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {currentTab === "labtests" && (
                <div>
                  <div className="mb-3">
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={addLabTest}
                    >
                      <i className="bi bi-plus-lg me-1"></i>
                      Add Lab Test
                    </button>
                  </div>

                  {labTests.length === 0 ? (
                    <p className="text-muted text-center">No lab tests added</p>
                  ) : (
                    labTests.map((test, idx) => (
                      <div key={idx} className="lab-test-card">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <h6 className="mb-0">Test #{idx + 1}</h6>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => removeLabTest(idx)}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                        <div className="row">
                          <div className="col-md-8 mb-2">
                            <label className="form-label small">
                              Test Name
                            </label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="Specific test name"
                              value={test.testName}
                              onChange={(e) =>
                                updateLabTest(idx, "testName", e.target.value)
                              }
                              maxLength={200} // ✅ Add max length
                            />
                          </div>
                          <div className="col-md-12 mb-2">
                            <label className="form-label small">
                              <i className="bi bi-clipboard-check me-1"></i>
                              Lab Result Remarks
                            </label>
                            <textarea
                              className="form-control form-control-sm"
                              rows="2"
                              placeholder="Enter lab result findings..."
                              value={test.resultRemarks || ""}
                              onChange={(e) =>
                                updateLabTest(
                                  idx,
                                  "resultRemarks",
                                  e.target.value
                                )
                              }
                              maxLength={1000} // ✅ Add max length
                            />
                          </div>
                          <div className="col-md-12">
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={test.urgent}
                                onChange={(e) =>
                                  updateLabTest(idx, "urgent", e.target.checked)
                                }
                              />
                              <label className="form-check-label small">
                                Mark as Urgent
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {currentTab === "referral" && (
                <div>
                  <div className="mb-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={isReferring}
                        onChange={(e) => setIsReferring(e.target.checked)}
                      />
                      <label className="form-check-label fw-bold">
                        Refer to Another Doctor
                      </label>
                    </div>
                  </div>

                  {isReferring && (
                    <>
                      <div className="mb-3">
                        <label className="form-label fw-bold">
                          Select Doctor
                        </label>
                        <select
                          className="form-select"
                          value={referralDoctor}
                          onChange={(e) => setReferralDoctor(e.target.value)}
                        >
                          <option value="">Select a doctor</option>
                          {doctorsList.map((doc) => (
                            <option key={doc._id} value={doc._id}>
                              Dr. {doc.name} - {doc.specialization} (
                              {doc.department})
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="mb-3">
                        <label className="form-label fw-bold">
                          Referral Reason
                        </label>
                        <textarea
                          className="form-control"
                          rows="3"
                          placeholder="Reason for referral"
                          value={referralReason}
                          onChange={handleReferralReasonChange} // ✅ Use sanitized handler
                          maxLength={500} // ✅ Add max length
                        ></textarea>
                      </div>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-footer-custom">
          {isEditing ? (
            <>
              <button
                className="btn btn-secondary"
                onClick={() => setIsEditing(false)}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                className="btn btn-success"
                onClick={handleUpdate}
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-circle me-1"></i>
                    Save Changes
                  </>
                )}
              </button>
            </>
          ) : (
            <button className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewEditRecordModal;